import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, interval, Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { TaskProgressWebSocketService } from './task-progress-websocket.service';

export interface Notification {
  id: string;
  user_id: string;
  project_id?: string;
  projectdata_id?: string;
  type: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

export interface NotificationListResponse {
  notifications: Notification[];
  total: number;
  unread_count: number;
  page: number;
  limit: number;
}

export interface UnreadCountResponse {
  unread_count: number;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService implements OnDestroy {
  private readonly baseUrl = environment.apiUrl + '/notifications';
  
  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();
  
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();
  
  private pollingInterval?: Subscription;
  private wsSubscription?: Subscription;
  private readonly POLLING_INTERVAL = 20000; // 20 seconds
  
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private taskProgressWs: TaskProgressWebSocketService
  ) {
    this.startPolling();
  }
  
  ngOnDestroy(): void {
    this.stopPolling();
  }
  
  /**
   * Start polling for unread count (or WebSocket when useWebSocket).
   * Polling code kept for rollback – do not remove.
   */
  startPolling(): void {
    if (!this.authService.getToken()) return;

    if (this.taskProgressWs.useWebSocket) {
      if (this.wsSubscription) return;
      this.taskProgressWs.connect();
      this.fetchUnreadCount(); // initial load via REST
      this.wsSubscription = this.taskProgressWs.notificationMessages$.subscribe((msg) => {
        const count = (msg.data?.['unread_count'] as number) ?? 0;
        this.unreadCountSubject.next(count);
      });
      console.log('🔔 Started notification updates via WebSocket');
      return;
    }

    if (this.pollingInterval) return;
    this.fetchUnreadCount();
    this.pollingInterval = interval(this.POLLING_INTERVAL).subscribe(() => {
      this.fetchUnreadCount();
    });
    console.log('🔔 Started notification polling');
  }

  /**
   * Stop polling for unread count (or WebSocket subscription).
   */
  stopPolling(): void {
    if (this.wsSubscription) {
      this.wsSubscription.unsubscribe();
      this.wsSubscription = undefined;
      console.log('🔔 Stopped notification WebSocket subscription');
    }
    if (this.pollingInterval) {
      this.pollingInterval.unsubscribe();
      this.pollingInterval = undefined;
      console.log('🔔 Stopped notification polling');
    }
  }
  
  /**
   * Fetch unread count from server
   */
  private fetchUnreadCount(): void {
    // If token is missing, stop polling and do not call API
    if (!this.authService.getToken()) {
      this.stopPolling();
      return;
    }
    
    console.log(`🔔 Calling unread-count API; interval=${this.POLLING_INTERVAL} ms`);
    
    this.http.get<UnreadCountResponse>(`${this.baseUrl}/unread-count`).subscribe({
      next: (response) => {
        this.unreadCountSubject.next(response.unread_count);
      },
      error: (error) => {
        console.error('Error fetching unread count:', error);
        // Don't update count on error, keep previous value
      }
    });
  }
  
  /**
   * Get unread count (Observable)
   */
  getUnreadCount(): Observable<number> {
    return this.unreadCountSubject.asObservable();
  }
  
  /**
   * Get notifications list
   */
  getNotifications(page: number = 1, limit: number = 20, unreadOnly: boolean = false): Observable<NotificationListResponse> {
    const params: any = { page, limit };
    if (unreadOnly) {
      params.unread_only = true;
    }
    
    return this.http.get<NotificationListResponse>(`${this.baseUrl}/`, { params });
  }
  
  /**
   * Fetch and update notifications list
   */
  fetchNotifications(page: number = 1, limit: number = 20, unreadOnly: boolean = false): void {
    this.getNotifications(page, limit, unreadOnly).subscribe({
      next: (response) => {
        this.notificationsSubject.next(response.notifications);
        this.unreadCountSubject.next(response.unread_count);
      },
      error: (error) => {
        console.error('Error fetching notifications:', error);
      }
    });
  }
  
  /**
   * Mark notification as read
   */
  markAsRead(notificationId: string): Observable<any> {
    return this.http.put(`${this.baseUrl}/${notificationId}/read`, {});
  }
  
  /**
   * Mark all notifications as read
   */
  markAllAsRead(): Observable<any> {
    return this.http.put(`${this.baseUrl}/read-all`, {});
  }
  
  /**
   * Get current unread count value
   */
  getCurrentUnreadCount(): number {
    return this.unreadCountSubject.value;
  }
  
  /**
   * Get current notifications
   */
  getCurrentNotifications(): Notification[] {
    return this.notificationsSubject.value;
  }
}





